import { PlusCircleOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import { App, Button, Modal, Typography, theme } from 'antd';
import { useForm } from 'antd/es/form/Form';
import type React from 'react';
import { useState } from 'react';
import type { FragmentType } from '../../../graphql/generated';
import { AisImportType, BillingPreference, DoctorSalutation } from '../../../graphql/generated/graphql.ts';
import { CREATE_DOCTOR_MUTATION } from './CreateDoctorModal.gql';
import { SET_DOCTOR_AIS_LINKS_MUTATION } from './DoctorAisLinks.gql';
import { DoctorForm, type DoctorFormValues } from './DoctorForm';
import type { CatalogFragment, InsuranceFragment, LabFragment, SpecialRateFragment, TagFragment, ThemeFragment } from './DoctorForm.gql';
import { createDoctorWithAisLinks } from './saveDoctorWithAisLinks';
export const CreateDoctorModal: React.FC<{
  allMeAddresses: string[];
  open: boolean;
  onClose: () => void;
  labFragments: FragmentType<typeof LabFragment>[];
  catalogFragments: FragmentType<typeof CatalogFragment>[];
  insuranceFragments: FragmentType<typeof InsuranceFragment>[];
  specialRateFragments: FragmentType<typeof SpecialRateFragment>[];
  themeFragments: FragmentType<typeof ThemeFragment>[];
  tagFragments: FragmentType<typeof TagFragment>[];
}> = ({
  allMeAddresses,
  open,
  onClose,
  labFragments,
  catalogFragments,
  insuranceFragments,
  specialRateFragments,
  themeFragments,
  tagFragments,
}) => {
  const { token } = theme.useToken();
  const [createDoctorMutation, { loading }] = useMutation(CREATE_DOCTOR_MUTATION);
  const [setAisLinks] = useMutation(SET_DOCTOR_AIS_LINKS_MUTATION);
  const [form] = useForm<DoctorFormValues>();
  const [hasError, setError] = useState(false);
  const { message } = App.useApp();

  const create = ({ aisLinks, ...input }: DoctorFormValues) =>
    createDoctorWithAisLinks(aisLinks, {
      saveDoctor: async () => (await createDoctorMutation({ variables: { input } })).data?.createDoctor?.id,
      saveLinks: (doctorId, links) => setAisLinks({ variables: { doctorId, links } }),
      message,
      close: closeAndReset,
    });

  const closeAndReset = () => {
    form.resetFields();
    setError(false);
    onClose();
  };

  return (
    <Modal
      width={1024}
      title="Neuen Zuweiser erstellen"
      open={open}
      destroyOnHidden
      onCancel={closeAndReset}
      footer={[
        <Typography.Text key="validationInfo" type="danger" style={{ marginRight: token.paddingSM }}>
          {hasError && 'Manche Felder sind nicht ordnungsgemäß ausgefüllt'}
        </Typography.Text>,
        <Button key="cancel" onClick={closeAndReset}>
          Abbrechen
        </Button>,
        <Button key="submit" icon={<PlusCircleOutlined />} type="primary" loading={loading} onClick={form.submit}>
          Erstellen
        </Button>,
      ]}
    >
      <DoctorForm
        allMeAddresses={allMeAddresses}
        form={form}
        initialValues={{
          salutation: DoctorSalutation.HERR,
          name: '',
          specialist: '',
          hvNumber: '',
          meAddress: '',
          firstName: '',
          lastName: '',
          preTitle: '',
          postTitle: '',
          street: '',
          zip: '',
          city: '',
          email: '',
          phone: '',
          assignedLabs: [],
          comment: '',
          aisImportType: AisImportType.GENERIC_ANSI,
          aisImportDoctorId: '',
          billingPreference: BillingPreference.ASK,
          preferSpecialRate: false,
          autoDiagnose: false,
          autoSpecialRate: false,
          catalogIds: [],
          tagIds: [],
          insuranceIds: [],
          specialRates: [],
          phoneContacts: [],
          doctorBilling: false,
          patientBilling: false,
          lgDoctorBilling: false,
          lgInsuranceIds: [],
          hasAis: true,
          printTransferDocument: false,
          showPrices: true,
          showPriceSuggestion: false,
          flipParamNames: false,
          addEmptyLabels: 0,
          aisExportTypes: [],
          themeId: null,
          csvImportEnabled: false,
          overrideRequestEnabled: false,
          disabled: false,
          ecardEnabled: false,
          aiEnabled: false,
          aiMonthlyTokenQuota: null,
          aiPackage: null,
          aiBillingLabId: null,
          installDate: null,
          zplLabelTemplate: '',
          zplLabelHeight: 25,
          zplLabelWidth: 50,
          addZplLabels: 0,
          aisLinks: [],
        }}
        onFinish={create}
        onFieldsChange={(_, allFields) => setError(allFields.some(it => it.errors?.length))}
        labFragments={labFragments}
        catalogFragments={catalogFragments}
        insuranceFragments={insuranceFragments}
        specialRateFragments={specialRateFragments}
        themeFragments={themeFragments}
        tagFragments={tagFragments}
      />
    </Modal>
  );
};
